import React, { useState, useEffect } from 'react';
import { Wrench, Plus, CheckCircle } from 'lucide-react';
import { Card, Btn, Modal, Table, Spinner, ErrBox } from '../../../components/UI';
import { fmtRp, fmtDate } from '../../../utils/format';
import { assetsAPI } from '../../../utils/api';

const TYPES = [
  { value: 'preventive', label: 'Preventif (Rutin)' },
  { value: 'corrective', label: 'Korektif (Perbaikan)' },
  { value: 'calibration', label: 'Kalibrasi' },
  { value: 'inspection', label: 'Inspeksi' },
];

const inputStyle = { padding: "9px 12px", border: "1.5px solid #E2E8F0", borderRadius: 8, fontSize: 13, outline: "none", fontFamily: "inherit", width: "100%", boxSizing: "border-box" };

export default function MaintenanceTab() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [err, setErr] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [saving, setSaving] = useState(false);
  const [assets, setAssets] = useState([]);
  const [editRow, setEditRow] = useState(null);

  const [form, setForm] = useState({
    asset_id: '', maintenance_type: 'preventive', scheduled_date: '',
    cost: '', notes: '', performed_by: ''
  });
  const [editForm, setEditForm] = useState({
    completion_date: '', cost: '', notes: ''
  });

  const loadData = async () => {
    setLoading(true);
    setErr('');
    try {
      const r = await assetsAPI.getAllMaintenance();
      setData(r.data || []);
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  };

  const loadAssets = async () => {
    try {
      const r = await assetsAPI.getAll();
      setAssets(r.data || []);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { loadData(); loadAssets(); }, []);

  const handleAdd = async () => {
    if (!form.asset_id) return;
    setSaving(true);
    try {
      await assetsAPI.addMaintenance(form.asset_id, {
        maintenance_type: form.maintenance_type,
        scheduled_date: form.scheduled_date,
        cost: form.cost ? Number(form.cost) : 0,
        notes: form.notes,
        performed_by: form.performed_by,
      });
      setShowAdd(false);
      setForm({ asset_id: '', maintenance_type: 'preventive', scheduled_date: '', cost: '', notes: '', performed_by: '' });
      loadData();
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  const handleEdit = async () => {
    setSaving(true);
    try {
      await assetsAPI.updateMaint(editRow.id, {
        completion_date: editForm.completion_date || null,
        cost: editForm.cost ? Number(editForm.cost) : 0,
        notes: editForm.notes,
      });
      setShowEdit(false);
      loadData();
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  const openEdit = (row) => {
    setEditRow(row);
    setEditForm({
      completion_date: row.completion_date ? row.completion_date.split('T')[0] : '',
      cost: row.cost || '',
      notes: row.notes || '',
    });
    setShowEdit(true);
  };

  const typeLabel = (v) => TYPES.find(t => t.value === v)?.label || v;
  const typeColor = (v) => ({
    preventive: '#3B82F6',
    corrective: '#EF4444',
    calibration: '#8B5CF6',
    inspection: '#F59E0B',
  })[v] || '#6B7280';

  return (
    <div>
      <Card>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "#111827" }}>Jadwal Pemeliharaan Aset</h3>
            <p style={{ margin: "4px 0 0", fontSize: 13, color: "#6B7280" }}>Kelola jadwal pemeliharaan berkala dan riwayat perbaikan</p>
          </div>
          <Btn icon={<Plus size={14}/>} onClick={() => setShowAdd(true)}>Tambah Pemeliharaan</Btn>
        </div>
        {err && <div style={{marginBottom: 12}}><ErrBox msg={err} onRetry={loadData}/></div>}
      </Card>

      <Card>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "#111827" }}>Riwayat Pemeliharaan ({data.length})</h3>
        </div>
        {loading ? <Spinner/> : (
          <Table pageSize={10} cols={[
            { key: "asset_code", label: "Kode Aset", nowrap: true, render: v => <span style={{fontFamily:"monospace",fontSize:12}}>{v || '—'}</span> },
            { key: "asset_name", label: "Nama Aset", render: v => <span style={{fontSize:13}}>{v || 'Aset tidak ditemukan'}</span> },
            { key: "maintenance_type", label: "Jenis", render: v => (
              <span style={{ padding:"3px 10px", borderRadius:9999, fontSize:11, fontWeight:600, color:"white", background: typeColor(v) }}>
                {typeLabel(v)}
              </span>
            )},
            { key: "scheduled_date", label: "Jadwal", render: v => fmtDate(v) },
            { key: "completion_date", label: "Selesai", render: v => v ? fmtDate(v) : <span style={{color:"#9CA3AF",fontSize:12}}>Belum</span> },
            { key: "cost", label: "Biaya", align: "right", render: v => <span style={{fontFamily:"monospace",fontSize:12}}>{v ? fmtRp(Number(v)) : '—'}</span> },
            { key: "notes", label: "Catatan", render: v => <span style={{fontSize:12,color:"#6B7280",maxWidth:200,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",display:"block"}}>{v || '—'}</span> },
            { key: "actions", label: "", render: (_, row) => (
              <div style={{ display:"flex", gap: 8 }}>
                {!row.completion_date && (
                  <button onClick={() => openEdit(row)} style={{ padding:"4px 10px", background:"#10B981", color:"white", border:"none", borderRadius:6, fontSize:12, cursor:"pointer", display:"flex", alignItems:"center", gap:4 }}>
                    <CheckCircle size={12}/> Tuntaskan
                  </button>
                )}
                {row.completion_date && (
                  <span style={{ padding:"4px 10px", background:"#D1FAE5", color:"#047857", borderRadius:6, fontSize:12, fontWeight:600 }}>✓ Selesai</span>
                )}
              </div>
            )},
          ]} data={data} emptyMsg="Belum ada data pemeliharaan"/>
        )}
      </Card>

      {showAdd && (
        <Modal title="Tambah Pemeliharaan" onClose={() => setShowAdd(false)}>
          <div>
            <p style={{ fontSize: 13, color: "#6B7280", marginBottom: 20 }}>
              Tambah jadwal atau riwayat pemeliharaan aset.
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div>
                <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 6 }}>Aset <span style={{color:"#EF4444"}}>*</span></label>
                <select value={form.asset_id} onChange={e => setForm({...form, asset_id: e.target.value})} style={inputStyle}>
                  <option value="">— Pilih Aset —</option>
                  {assets.map(a => <option key={a.id} value={a.id}>{a.code} — {a.name}</option>)}
                </select>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                <div>
                  <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 6 }}>Jenis</label>
                  <select value={form.maintenance_type} onChange={e => setForm({...form, maintenance_type: e.target.value})} style={inputStyle}>
                    {TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 6 }}>Tanggal Jadwal</label>
                  <input type="date" value={form.scheduled_date} onChange={e => setForm({...form, scheduled_date: e.target.value})} style={inputStyle}/>
                </div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                <div>
                  <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 6 }}>Biaya (Rp)</label>
                  <input type="number" placeholder="0" value={form.cost} onChange={e => setForm({...form, cost: e.target.value})} style={inputStyle}/>
                </div>
                <div>
                  <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 6 }}>Dilakukan Oleh</label>
                  <input type="text" placeholder="Nama teknisi" value={form.performed_by} onChange={e => setForm({...form, performed_by: e.target.value})} style={inputStyle}/>
                </div>
              </div>
              <div>
                <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 6 }}>Catatan</label>
                <textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} style={{...inputStyle, resize: "vertical", minHeight: 80}}/>
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 24 }}>
              <Btn variant="secondary" onClick={() => setShowAdd(false)}>Batal</Btn>
              <Btn icon={<Wrench size={14}/>} onClick={handleAdd} disabled={saving || !form.asset_id}>
                {saving ? "Menyimpan…" : "Simpan"}
              </Btn>
            </div>
          </div>
        </Modal>
      )}

      {showEdit && editRow && (
        <Modal title="Tuntaskan Pemeliharaan" onClose={() => setShowEdit(false)}>
          <div>
            <div style={{ padding: "10px 14px", background: "#EFF6FF", borderRadius: 8, marginBottom: 20, border: "1px solid #BFDBFE" }}>
              <p style={{ margin: 0, fontSize: 13, color: "#1E40AF" }}>
                <strong>{typeLabel(editRow.maintenance_type)}</strong> — {editRow.asset_name || editRow.asset_code || 'Aset'} ({fmtDate(editRow.scheduled_date)})
              </p>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div>
                <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 6 }}>Tanggal Selesai</label>
                <input type="date" value={editForm.completion_date} onChange={e => setEditForm({...editForm, completion_date: e.target.value})} style={inputStyle}/>
              </div>
              <div>
                <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 6 }}>Biaya (Rp)</label>
                <input type="number" placeholder="0" value={editForm.cost} onChange={e => setEditForm({...editForm, cost: e.target.value})} style={inputStyle}/>
              </div>
              <div>
                <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 6 }}>Catatan</label>
                <textarea value={editForm.notes} onChange={e => setEditForm({...editForm, notes: e.target.value})} style={{...inputStyle, resize: "vertical", minHeight: 80}}/>
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 24 }}>
              <Btn variant="secondary" onClick={() => setShowEdit(false)}>Batal</Btn>
              <Btn icon={<CheckCircle size={14}/>} onClick={handleEdit} disabled={saving}>
                {saving ? "Menyimpan…" : "Tuntaskan"}
              </Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
